import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  BarChart3, 
  Eye, 
  Smartphone, 
  Download, 
  MousePointer,
  TrendingUp,
  Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  const isAdmin = user?.email === 'scott@badasshair.com' || user?.is_admin;

  useEffect(() => {
    if (!isAdmin) return;
    
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/admin/analytics?days=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(response.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        toast.error('Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [isAdmin, period]);

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <p className="text-stone-600">Access denied. Admin only.</p>
        </div>
      </DashboardLayout>
    );
  }

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card className="border-stone-100 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-stone-600 text-sm mb-1">{title}</p>
            <p className="text-3xl font-bold text-stone-900">{loading ? '—' : value.toLocaleString()}</p>
            {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-heading text-2xl font-semibold text-stone-900">Analytics Dashboard</h1>
              <p className="text-stone-600">Track app usage and engagement</p>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-stone-500" />
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="h-10 px-3 rounded-lg border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#5F8D4E]"
              data-testid="period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Page Views"
            value={analytics?.total_page_views || 0}
            icon={Eye}
            color="bg-blue-500"
            subtitle="Website visits"
          />
          <StatCard
            title="App Opens"
            value={analytics?.total_app_opens || 0}
            icon={Smartphone}
            color="bg-green-500"
            subtitle="App launches"
          />
          <StatCard
            title="App Downloads"
            value={analytics?.total_app_downloads || 0}
            icon={Download}
            color="bg-purple-500"
            subtitle="PWA installs"
          />
          <StatCard
            title="Total Clicks"
            value={analytics?.total_clicks || 0}
            icon={MousePointer}
            color="bg-orange-500"
            subtitle="User interactions"
          />
        </div>

        {/* Top Pages */}
        <Card className="border-stone-100 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-stone-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#5F8D4E]" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-stone-500 text-center py-4">Loading...</p>
            ) : analytics?.top_pages?.length > 0 ? (
              <div className="space-y-3">
                {analytics.top_pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                    <span className="text-stone-700">{page.page || 'Home'}</span>
                    <span className="font-semibold text-stone-900">{page.views.toLocaleString()} views</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-4">No page view data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Daily Stats */}
        <Card className="border-stone-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-stone-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#D97757]" />
              Daily Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-stone-500 text-center py-4">Loading...</p>
            ) : analytics?.daily_stats?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-3 px-4 text-stone-600 font-medium">Date</th>
                      <th className="text-right py-3 px-4 text-stone-600 font-medium">Page Views</th>
                      <th className="text-right py-3 px-4 text-stone-600 font-medium">App Opens</th>
                      <th className="text-right py-3 px-4 text-stone-600 font-medium">Downloads</th>
                      <th className="text-right py-3 px-4 text-stone-600 font-medium">Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.daily_stats.slice(-10).reverse().map((day, index) => (
                      <tr key={index} className="border-b border-stone-100 hover:bg-stone-50">
                        <td className="py-3 px-4 text-stone-900">{day.date}</td>
                        <td className="text-right py-3 px-4 text-stone-700">{day.page_views}</td>
                        <td className="text-right py-3 px-4 text-stone-700">{day.app_opens}</td>
                        <td className="text-right py-3 px-4 text-stone-700">{day.app_downloads}</td>
                        <td className="text-right py-3 px-4 text-stone-700">{day.clicks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-stone-500 text-center py-4">No daily data yet. Analytics will appear as users visit the app.</p>
            )}
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Analytics data is collected automatically when users visit the app. 
            Page views, app opens (PWA launches), and app downloads (PWA installs) are tracked anonymously.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
